import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { authService } from '../services/authService';
import { MARKETING_EMAIL_CONSENT } from '../constants/consent';
import { ConsentText } from './ConsentText';
import { CONSENT_PREFERENCES_CLASS, isConsentManagerPresent } from '../services/consentService';
import { ToggleSwitch } from './shared/ToggleSwitch';
// Advertising-audience consent is not offered anywhere yet; the backend keeps
// supporting it so the row can return alongside the signup checkbox.
const rows = [
    { key: 'marketingEmail', title: 'Tips, workshops, and offers', wording: MARKETING_EMAIL_CONSENT },
];
const sameChoice = (a, b) => rows.every((row) => a[row.key] === b[row.key]);
// Settings > Communications: the person's own consent. A switch sets a pending
// choice; nothing is recorded or sent to the newsletter list until Save in the
// footer commits it. Flipping back and forth before saving changes nothing.
export const CommunicationsSettings = forwardRef(({ onDirtyChange }, ref) => {
    const [saved, setSaved] = useState(null);
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const dirty = saved !== null && draft !== null && !sameChoice(saved, draft);
    useEffect(() => {
        onDirtyChange(dirty);
    }, [dirty, onDirtyChange]);
    useEffect(() => {
        let cancelled = false;
        authService
            .getMyCommunicationPreferences()
            .then((p) => {
            if (cancelled)
                return;
            setSaved(p);
            setDraft(p);
        })
            .catch(() => {
            if (!cancelled)
                setError('Could not load your communication settings. Close Settings and try again.');
        });
        return () => {
            cancelled = true;
            onDirtyChange(false);
        };
        // The loader runs once when the tab opens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useImperativeHandle(ref, () => ({
        save: async () => {
            if (!draft || !dirty || saving)
                return false;
            setSaving(true);
            setError('');
            try {
                const recorded = await authService.updateMyCommunicationPreferences(draft);
                setSaved(recorded);
                setDraft(recorded);
                return true;
            }
            catch (_a) {
                setError('Could not save that change. Please try again.');
                return false;
            }
            finally {
                setSaving(false);
            }
        },
    }), [draft, dirty, saving]);
    return (_jsxs("div", { className: "space-y-5", children: [rows.map((row, i) => (_jsxs("div", { className: i > 0 ? 'pt-4 border-t' : '', style: i > 0 ? { borderColor: 'var(--pewter)' } : undefined, children: [_jsxs("div", { className: "flex items-center justify-between gap-4", children: [_jsx("span", { className: "text-sm font-semibold", style: { color: 'var(--charcoal)' }, children: row.title }), draft && (_jsx("button", { type: "button", role: "switch", "aria-checked": draft[row.key], "aria-label": row.title, onClick: () => setDraft({ ...draft, [row.key]: !draft[row.key] }), disabled: saving, className: "flex-shrink-0 disabled:cursor-not-allowed", children: _jsx(ToggleSwitch, { checked: draft[row.key] }) }))] }), _jsx("p", { className: "text-xs mt-2", style: { color: 'var(--charcoal)' }, children: _jsx(ConsentText, { wording: row.wording }) })] }, row.key))), dirty && !error && (_jsx("p", { className: "text-xs", style: { color: 'var(--forest)' }, children: "Your choice is not recorded until you press Save changes." })), error && _jsx("p", { className: "text-xs text-red-600", children: error }), _jsx("p", { className: "text-xs pt-4 border-t", style: { color: 'var(--pewter)', borderColor: 'var(--pewter)' }, children: "Account, project, billing, and security emails always arrive, whatever you choose here." }), isConsentManagerPresent() && (_jsxs("div", { className: "pt-4 border-t", style: { borderColor: 'var(--pewter)' }, children: [_jsxs("div", { className: "flex items-center justify-between gap-4", children: [_jsx("span", { className: "text-sm font-semibold", style: { color: 'var(--charcoal)' }, children: "Cookies" }), _jsx("button", { type: "button", className: `${CONSENT_PREFERENCES_CLASS} text-xs font-semibold hover:underline transition-colors`, style: { color: 'var(--forest)' }, children: "Cookie settings" })] }), _jsx("p", { className: "text-xs mt-2", style: { color: 'var(--charcoal)' }, children: "Choose which analytics and marketing cookies LandScope may use on this browser." })] }))] }));
});
CommunicationsSettings.displayName = 'CommunicationsSettings';
